import { ReportOptions } from '@showbie-socrative/socrative-utils';

export const EMAIL_ME = 'EMAIL_ME';
export const FORM = 'FORM';
export const SAVE_TO_DRIVE = 'SAVE_TO_DRIVE';
export const DOWNLOADING = 'DOWNLOADING';
export const PREPARING = 'PREPARING';
export const ERROR = 'ERROR';
export const UPDATE_OPTION = 'UPDATE_OPTION';
export const BEGIN_ASYNC = 'BEGIN_ASYNC';
export const COMPLETE_ASYNC = 'COMPLETE_ASYNC';

type EmailMe = { type: typeof EMAIL_ME };
type Form = { type: typeof FORM };
type SaveToDrive = { type: typeof SAVE_TO_DRIVE };
type Preparing = { type: typeof PREPARING; payload: { url: string } };
type Downloading = { type: typeof DOWNLOADING; payload: { url: string } };
type Error = { type: typeof ERROR; payload: { error: any } };
type BeginAsync = { type: typeof BEGIN_ASYNC };
type CompleteAsync = { type: typeof COMPLETE_ASYNC };
type UpdateOption = {
  type: typeof UPDATE_OPTION;
  payload: { identifier: ReportOptions; value: boolean };
};

export type ExportReportDispatch = React.Dispatch<ExportReportAction>;

export enum ExportView {
  FORM = 'FORM',
  PREPARING = 'PREPARING',
  DOWNLOADING = 'DOWNLOADING',
  EMAIL_ME = 'EMAIL_ME',
  SAVE_TO_DRIVE = 'SAVE_TO_DRIVE',
  ERROR = 'ERROR',
}

export interface ExportReportState {
  exportView: ExportView;
  preparedUrl: string;
  error: any;
  isFetching: boolean;
  optionState: ReportOptionState;
}

export type ExportReportAction =
  | EmailMe
  | Form
  | SaveToDrive
  | Preparing
  | Downloading
  | Error
  | UpdateOption
  | BeginAsync
  | CompleteAsync;

export const toPreparing =
  (dispatch: React.Dispatch<ExportReportAction>) => (url: string) =>
    dispatch({ type: 'PREPARING', payload: { url } });

export const toDownloading =
  (dispatch: React.Dispatch<ExportReportAction>) => (url: string) =>
    dispatch({ type: 'DOWNLOADING', payload: { url } });

export const toEmailMe = (dispatch: React.Dispatch<ExportReportAction>) => () =>
  dispatch({ type: 'EMAIL_ME' });

export const toError =
  (dispatch: React.Dispatch<ExportReportAction>) => (error: any) =>
    dispatch({ type: 'ERROR', payload: { error } });

export const toForm = (dispatch: React.Dispatch<ExportReportAction>) => () =>
  dispatch({ type: 'FORM' });

export const toSaveToDrive =
  (dispatch: React.Dispatch<ExportReportAction>) => () =>
    dispatch({ type: 'SAVE_TO_DRIVE' });

export const updateOptionTo =
  (dispatch: React.Dispatch<ExportReportAction>) =>
  ({ identifier, value }: { identifier: ReportOptions; value: boolean }) =>
    dispatch({
      type: 'UPDATE_OPTION',
      payload: { identifier, value },
    });

export const beginAsync =
  (dispatch: React.Dispatch<ExportReportAction>) => () =>
    dispatch({ type: BEGIN_ASYNC });

export const completeAsync =
  (dispatch: React.Dispatch<ExportReportAction>) => () =>
    dispatch({ type: COMPLETE_ASYNC });

export type ReportOptionState = {
  [key in ReportOptions]?: boolean;
};

export const initialState = (
  initialOptions: ReportOptionState
): ExportReportState => ({
  exportView: ExportView.FORM,
  preparedUrl: '',
  error: null,
  isFetching: false,
  optionState: initialOptions,
});

export const ReportOptionHookReducer = (
  state: ExportReportState,
  action: ExportReportAction
): ExportReportState => {
  switch (action.type) {
    case PREPARING: {
      const {
        payload: { url },
      } = action;
      return { ...state, exportView: ExportView.PREPARING, preparedUrl: url };
    }
    case DOWNLOADING: {
      const {
        payload: { url },
      } = action;
      const { exportView } = state;

      return exportView === ExportView.PREPARING
        ? { ...state, exportView: ExportView.DOWNLOADING, preparedUrl: url }
        : state;
    }
    case EMAIL_ME:
      return { ...state, exportView: ExportView.EMAIL_ME };
    case FORM:
      return {
        ...state,
        exportView: ExportView.FORM,
        preparedUrl: '',
        isFetching: false,
      };
    case SAVE_TO_DRIVE:
      return { ...state, exportView: ExportView.SAVE_TO_DRIVE };
    case UPDATE_OPTION: {
      const {
        payload: { identifier, value },
      } = action;
      const { optionState } = state;
      const newOptionState = { ...optionState, [identifier]: value };
      return { ...state, optionState: newOptionState };
    }
    case ERROR: {
      const {
        payload: { error },
      } = action;
      return { ...state, exportView: ExportView.ERROR, error };
    }
    case BEGIN_ASYNC: {
      return { ...state, isFetching: true };
    }
    case COMPLETE_ASYNC: {
      return { ...state, isFetching: false };
    }
    default:
      throw new Error('Unreachable case');
  }
};
